import { format } from 'date-fns'
import { showApiError, showSuccess, showValidationMessage } from '@/lib/utils/notifications'
import { EntityEditTabBar } from '@/components/ui/EntityEditTabBar'
import {
  entityActionButtonClusterClass,
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import detailStyles from '@/features/reimbursements/styles/reimbursementDetail.module.css'
import { RESIDENT_POLICIES } from '../../constants/residentConstants'
import {
  canShowCalculationContentPanel,
  canShowReimbursementReviewBlock,
  isCalculationPanelLocked,
  isPublicNotesComposeDisabled,
  isResidentDetailSaveDisabled,
  resolveCalculationPaymentHistoryFlags,
  shouldShowSaveBar,
} from '../../utils/residentValidation'
import type {
  DocumentHistoryRow,
  LibertyDocumentRow,
  OldStatementRow,
  ResidentDetailTab,
  ResidentDocumentGroup,
} from '../../types'
import type { ResidentDetailSectionProps } from '../../types/detailPage'
import { DocumentHistoryTable } from './DocumentHistoryTable'
import { LibertyInternalDocumentsSection } from './LibertyInternalDocumentsSection'
import { PaymentReviewLogSection } from './PaymentReviewLogSection'
import { ResidentCalculationTabForm } from './ResidentCalculationTabForm'
import { ResidentDefaultForm } from './ResidentDefaultForm'
import { ReimbursementRequestTab } from '@/features/reimbursements/components/detail/ReimbursementRequestTab'
import { getRemovingDefaultAttachmentId } from '@/features/resident/utils/residentDocuments'
import { canShowCancelReimbursementButton } from '@/features/resident/utils/residentValidation'
import { ResidentPublicNotesMessagesCard } from './ResidentPublicNotesMessagesCard'
import { ResidentReimbursementReviewSection } from './ResidentReimbursementReviewSection'
import { ResidentHeaderSummary } from './ResidentHeaderSummary'
import { ResidentInfoSection } from './ResidentInfoSection'
import { ResidentNotesSection } from './ResidentNotesSection'
import { ResidentPmsIntegrationTab } from './ResidentPmsIntegrationTab'
import { isExcludedNoteRole } from '../../constants/residentConstants'
import { formatCurrency } from '@/features/reimbursements/utils/formatters'

function reimbursementStatementRows(
  document: ResidentDocumentGroup | undefined,
): OldStatementRow[] {
  const raw = document?.old_statement as
    | OldStatementRow[]
    | Record<string, OldStatementRow>
    | undefined
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return Object.values(raw).filter(Boolean) as OldStatementRow[]
}

export function ResidentDetailFormContent({ ctx }: ResidentDetailSectionProps) {
  const rentalStatus = ctx.detail.residentDetail?.resident_default?.rental_status
  const residentDefault = ctx.detail.residentDetail?.resident_default
  const isNotesOnResidentInfo = rentalStatus && ['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(rentalStatus)
  const paymentHistoryFlags = resolveCalculationPaymentHistoryFlags(
    (ctx.detail.residentDetail?.payment_history || []) as Array<Record<string, unknown>>,
  )
  const showCalculationContent = canShowCalculationContentPanel({
    roleName: ctx.role.roleName,
    rentalStatus: ctx.values.rental_status || rentalStatus,
    rentalProgress:
      ctx.values.rental_progress || residentDefault?.rental_progress,
    libertyApproved: paymentHistoryFlags.libertyApproved,
    allhasCheckNumber: paymentHistoryFlags.allhasCheckNumber,
  })
  const publicNotesDisabled = isPublicNotesComposeDisabled(
    ctx.role.roleName,
    residentDefault,
    ctx.detail.applicationData?.progress_status,
    ctx.selectedTab,
  )
  const saveDisabled =
    !ctx.form.formState.isValid ||
    ctx.detail.saving ||
    isResidentDetailSaveDisabled(ctx.role.roleName, ctx.formDisabled, residentDefault, ctx.selectedTab)
  const publicNotesSection = !ctx.isExternalInsurance ? (
    <ResidentPublicNotesMessagesCard
      register={ctx.form.register}
      messageValue={ctx.values.message || ''}
      mergedRows={ctx.publicNotesMergedRows}
      loading={ctx.publicNotesLoading}
      saving={ctx.publicMessageSaving}
      disabled={publicNotesDisabled}
      showMessageBox={ctx.role.hasPolicy([RESIDENT_POLICIES.propertyLibertyMessageWrite])}
      // Angular reimbursement tab: hide Save + readonly for excludedNoteRoles (e.g. ROLE_LIBERTY_SALES).
      // Resident Info notes block does not apply that gate.
      showSaveButton={Boolean(isNotesOnResidentInfo) || !isExcludedNoteRole(ctx.role.roleName)}
      messageReadOnly={!isNotesOnResidentInfo && isExcludedNoteRole(ctx.role.roleName)}
      onSave={ctx.handleSavePublicMessage}
      onNotesUpdated={() => ctx.reloadPublicNotes({ silent: true })}
    />
  ) : null

  return (
    <>
      <ResidentHeaderSummary
        pageType={ctx.pageType}
        applicationData={ctx.detail.applicationData}
        residentDetail={ctx.detail.residentDetail}
        statusLabelMaps={ctx.detail.statusLabelMaps}
        routeApplicantId={ctx.applicantId}
        roleName={ctx.role.roleName}
        canManageExcludeAuto={['ROLE_ADMIN', 'ROLE_LIBERTY_DEFAULTS'].includes(ctx.role.roleName)}
        onExcludeAuto={(checked) =>
          void ctx.detail
            .setExcludeAuto(checked)
            .then(() => showSuccess('Reimbursement Updated Successfully'))
            .catch(showApiError)
        }
        onAccessConfirmation={ctx.handleAccessConfirmation}
        accessConfirmationLoading={ctx.accessConfirmationLoading}
        hasPolicy={ctx.role.hasPolicy}
        showActions
        formValues={{
          lease_end_date: ctx.values.lease_end_date,
          rental_status_date: ctx.values.rental_status_date,
          rental_status: ctx.values.rental_status,
          adjusted_notice: ctx.values.adjusted_notice,
          moveout_date: ctx.values.moveout_date,
        }}
      />
      <form
        onSubmit={(event) => void ctx.save(event)}
        className={`${detailStyles.cardContainer} flex flex-col !p-0 overflow-visible`}
        data-testid={`resident-detail-form-${ctx.selectedTab}`}
      >
        <div className="min-w-0 border-b border-slate-200 bg-slate-50/40 pt-2" data-testid="resident-detail-tabs-card">
          <EntityEditTabBar
            tabs={ctx.visibleTabs}
            activeTab={ctx.selectedTab}
            onSelect={(tabId) => ctx.changeTab(tabId as ResidentDetailTab)}
            navTestId="resident-detail-tab-bar"
            ariaLabel="Resident detail sections"
            wrapClassName="px-2 sm:px-3"
          />
        </div>
        <div className="flex flex-col space-y-6 p-4 sm:p-6">
        {ctx.selectedTab === 'residentinfo' ? (
          <div className="space-y-6">
            <ResidentInfoSection
              applicationData={ctx.detail.applicationData}
              residentDetail={ctx.detail.residentDetail}
              statusLabelMaps={ctx.detail.statusLabelMaps}
              roleName={ctx.role.roleName}
              hasPolicy={ctx.role.hasPolicy}
              residentStatusForm={
                <ResidentDefaultForm
                  control={ctx.form.control}
                  register={ctx.form.register}
                  setValue={ctx.form.setValue}
                  values={ctx.values}
                  errors={ctx.form.formState.errors}
                  rentalList={ctx.detail.rentalList}
                  statusList={ctx.detail.statusList}
                  missingFields={[]}
                  disabled={ctx.formDisabled}
                  selectedTab="residentinfo"
                  roleName={ctx.role.roleName}
                  residentDefault={ctx.detail.residentDetail?.resident_default}
                  applicationData={ctx.detail.applicationData}
                  isLibertyRole={ctx.isLibertyRole}
                  onRentalStatusUserChange={ctx.handleRentalStatusUserChange}
                  showSectionTitle={false}
                />
              }
              residentInfoActions={
                shouldShowSaveBar('residentinfo') && !ctx.isExternalInsurance ? (
                  <button
                    type="submit"
                    data-testid="resident-detail-residentinfo-save-button"
                    disabled={saveDisabled}
                    className={primaryEntityActionButtonClass}
                  >
                    Save
                  </button>
                ) : null
              }
            />
            {isNotesOnResidentInfo ? publicNotesSection : null}
          </div>
        ) : null}
        {ctx.selectedTab === 'default' ? (
          <>
            <ReimbursementRequestTab
              control={ctx.form.control}
              register={ctx.form.register}
              setValue={ctx.form.setValue}
              values={ctx.values}
              errors={ctx.form.formState.errors}
              rentalList={ctx.detail.rentalList}
              statusList={ctx.detail.statusList}
              disabled={ctx.formDisabled}
              roleName={ctx.role.roleName}
              residentDefault={ctx.detail.residentDetail?.resident_default}
              reimbursementDetail={
                (ctx.detail.residentDetail?.reimbursement_detail ??
                  ctx.detail.residentDetail?.reimbursement) as Record<string, unknown> | undefined
              }
              applicationData={ctx.detail.applicationData}
              isLibertyRole={ctx.isLibertyRole}
              onRentalStatusDateUserChange={ctx.handleRentalStatusDateUserChange}
              onFirstMonthChange={ctx.handleFirstMonthChange}
              onOpenEorp={() => ctx.setShowEorpModal(true)}
              onImmediateDocumentUpload={ctx.uploadClaimDocumentImmediate}
              onReviewDocument={
                ctx.isLibertyRole ? ctx.reviewReimbursementDocument : undefined
              }
              onReclassifyDocument={
                ctx.isLibertyRole ? ctx.reclassifyReimbursementDocument : undefined
              }
              documentReviewIncompleteReasons={ctx.documentReviewIncompleteReasons}
              documentUploadingField={ctx.documentUploadingField}
              residentDocuments={ctx.detail.residentDetail?.document}
              onDownloadDocument={ctx.downloadResidentDocument}
              onRemoveDocument={(id) => ctx.setPendingDocumentRemove({ kind: 'default', id })}
              removingDocumentAttachmentId={getRemovingDefaultAttachmentId(
                ctx.documentsHook.processingKey,
              )}
              canUploadDocuments={ctx.role.hasPolicy([RESIDENT_POLICIES.defaultFilesView])}
              canRemoveDocuments={
                !ctx.isExternalInsurance &&
                ctx.role.hasPolicy([RESIDENT_POLICIES.defaultFilesRemove])
              }
              saveDisabled={saveDisabled}
              sectionSaveLoading={ctx.detail.saving}
              onSectionSave={() => {
                const formEl = document.querySelector<HTMLFormElement>(
                  `[data-testid="resident-detail-form-default"]`,
                )
                formEl?.requestSubmit()
              }}
              onRegisterExtendedFields={ctx.registerReimbursementExtendedFields}
              onSubmitReimbursementSection={ctx.submitReimbursementSectionByMilestone}
              onUnlockReimbursementSection={
                ctx.isLibertyRole ? ctx.unlockReimbursementSectionByMilestone : undefined
              }
              onSubmitForLibertyReview={
                ctx.isLibertyRole ? ctx.submitReimbursementForLibertyReviewAction : undefined
              }
              onCancelReimbursement={
                ctx.defaultId &&
                canShowCancelReimbursementButton(
                  ctx.role.roleName,
                  ctx.detail.residentDetail?.resident_default,
                )
                  ? ctx.handleCancelReimbursement
                  : undefined
              }
            />
            {publicNotesSection}
            {canShowReimbursementReviewBlock(
              ctx.role.roleName,
              ctx.detail.residentDetail?.resident_default?.reimbursement_type,
            ) ? (
              <ResidentReimbursementReviewSection
                roleName={ctx.role.roleName}
                residentDefault={ctx.detail.residentDetail?.resident_default}
                claimStatusLabels={ctx.detail.statusLabelMaps?.claim_status_label}
                disabled={ctx.formDisabled}
                onReviewReimbursement={ctx.handleOpenReviewReimbursement}
              />
            ) : null}
          </>
        ) : null}
        {ctx.selectedTab === 'default' ? (
          <div className="mt-6 space-y-6 border-t border-slate-200 pt-8">
            <DocumentHistoryTable
              rows={(ctx.detail.residentDetail?.documentHistory || []) as DocumentHistoryRow[]}
              onDownload={(row) =>
                void ctx.downloadResidentDocument('DOCUMENT_HISTORY', {
                  attachment_id: row.attachment_id,
                  filepath: String(row.filepath || row.file_path || ''),
                  filename: row.filename,
                })
              }
            />
          </div>
        ) : null}
        {ctx.selectedTab === 'calculation' ? (
          showCalculationContent ? (
          <div
            className="space-y-6"
            data-testid="resident-detail-calculation-content"
          >
            <ResidentCalculationTabForm
              control={ctx.form.control}
              register={ctx.form.register}
              setValue={ctx.form.setValue}
              values={ctx.values}
              errors={ctx.form.formState.errors}
              disabled={
                ctx.formDisabled ||
                isCalculationPanelLocked(ctx.role.roleName, ctx.values.rental_status)
              }
              roleName={ctx.role.roleName}
              residentDetail={ctx.detail.residentDetail}
              applicationData={ctx.detail.applicationData}
              publicNotesAndMessages={ctx.publicNotesMergedRows as Array<Record<string, unknown>>}
              isLibertyRole={ctx.isLibertyRole}
              isOverrideCleared={ctx.isOverrideCleared}
              onRentalStatusDateUserChange={ctx.handleRentalStatusDateUserChange}
              onFirstMonthChange={ctx.handleFirstMonthChange}
              userId={ctx.userId}
              applicationId={ctx.applicationId}
              currentUserDisplayName={ctx.currentUserDisplayName}
              onReimbursementEndDateOverrideSaved={async (savedDate, reason, response) => {
                ctx.markEndDateOverridePreserved()
                const fromApi = response?.resident_default as Record<string, unknown> | undefined
                // Angular saveReimbursementEndDateOverride:
                //   this.reimbursementEndDateOverrideUpdatedAt = moment().format('YYYY-MM-DD hh:mm:ss A')
                //   then loadResidentDetail(). Re-apply that local stamp after reload so the
                //   UTC API value cannot flip day 3 → day 2 a second later.
                const overrideAt = format(new Date(), 'yyyy-MM-dd hh:mm:ss a')
                const overrideBy =
                  (fromApi?.reimbursement_end_date_override_updated_by as string) ||
                  ctx.currentUserDisplayName
                const overridePatch = {
                  adjust_max_movein_date: savedDate,
                  reimbursement_end_date_override: '1',
                  reimbursement_end_date_override_source: 'Manual Override',
                  reimbursement_end_date_override_reason: reason,
                  reimbursement_end_date_override_updated_by: overrideBy,
                  reimbursement_end_date_override_at: overrideAt,
                }
                ctx.detail.patchResidentDefault({
                  ...(fromApi || {}),
                  ...overridePatch,
                })
                await ctx.reloadDetail()
                ctx.detail.patchResidentDefault(overridePatch)
              }}
              canConfigurePayment={ctx.isLibertyRole}
              onConfigurePayment={ctx.handleConfigurePayment}
              onDeny={() => ctx.requireL1ReviewThen(ctx.handleDenyClaim)}
              onReopen={() => ctx.requireL1ReviewThen(ctx.handleReopenClaim)}
              calculationSaveDisabled={
                !ctx.form.formState.isValid ||
                ctx.detail.saving ||
                ctx.formDisabled ||
                (!!ctx.detail.residentDetail?.resident_default?.review_decision &&
                  ![
                    'L1 Recalculated',
                    'L2 Recalculated',
                    'L2 Approval',
                    'L3 Approval',
                  ].includes(String(ctx.detail.residentDetail.resident_default.review_decision)))
              }
              claimStatusLabels={ctx.detail.statusLabelMaps?.claim_status_label}
            />

            {/* Angular payment RBAC: L1+L2 = resident_default_liberty_approval; L3 = external; delete = delete_payment; edit = liberty role */}
            <PaymentReviewLogSection
              key={ctx.approvalResetKey}
              residentDetail={ctx.detail.residentDetail}
              isLibertyRole={ctx.isLibertyRole}
              canL1Approve={ctx.role.hasPolicy([RESIDENT_POLICIES.defaultLibertyApproval])}
              canL2Approve={ctx.role.hasPolicy([RESIDENT_POLICIES.defaultLibertyApproval])}
              canL3Approve={ctx.role.hasPolicy([RESIDENT_POLICIES.defaultExternalApproval])}
              canDeletePayment={ctx.role.hasPolicy([RESIDENT_POLICIES.defaultDeletePayment])}
              canEditPayment={ctx.isLibertyRole}
              processing={ctx.paymentsHook.processing}
              onSubmitPaymentApproval={(id, field, value) => ctx.handlePaymentApproval(id, field, value)}
              onSubmitClaimApproval={(id, field, value) => ctx.handleClaimApproval(id, field, value)}
              onEditPayment={ctx.handleEditPayment}
              onDeletePayment={(row) => {
                const id = row.payment_log_id
                if (id)
                  ctx.setConfirm({
                    // Angular `deletePayment` copy.
                    title: 'Delete Payment Adjustment',
                    message: 'Do you want to Delete this Payment Adjustment?',
                    primaryLabel: 'Delete',
                    secondaryLabel: 'Cancel',
                    onPrimary: () => {
                      ctx.setConfirm(null)
                      void ctx.paymentsHook
                        .removePayment(id)
                        .then(() => showSuccess('Payment Adjustment Deleted Successfully'))
                        .catch(showApiError)
                    },
                  })
              }}
              onRejectClaim={ctx.rejectClaim}
            />

            {/* Angular Adjust Payment row: Total Paid + Adjust when L2/L3 names exist on payment_history[0] */}
            {ctx.canShowAdjustPayment ? (
              <div
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid="resident-detail-adjust-payment-row"
              >
                <p className="text-sm font-semibold text-slate-800">
                  Total Paid: {formatCurrency(ctx.totalPaid)}
                </p>
                <button
                  type="button"
                  onClick={ctx.handleAdjustPayment}
                  className={primaryEntityActionButtonClass}
                  data-testid="resident-detail-adjust-payment-button"
                >
                  Adjust Payment
                </button>
              </div>
            ) : null}
          </div>
          ) : (
            <div
              className="min-h-[8rem]"
              data-testid="resident-detail-calculation-content-empty"
              aria-hidden="true"
            />
          )
        ) : null}
        {ctx.selectedTab === 'libertyinternal' ? (
          ctx.isLibertyRole ? (
          <div
            className="space-y-4 sm:space-y-6"
            data-testid="resident-detail-libertyinternal-section"
          >
            <ResidentNotesSection
              notes={ctx.notesHook.notes}
              loading={ctx.notesHook.loading}
              hasPolicy={ctx.role.hasPolicy}
              draftValue={ctx.internalNotesDraft}
              onDraftChange={ctx.setInternalNotesDraft}
              isLibertyRole={ctx.isLibertyRole}
              loggedUserId={ctx.loggedUserId}
              noteSaveProcessing={ctx.internalNoteSaving}
              onSaveInternalNote={ctx.submitInternalNote}
              onNotesUpdated={() => {
                void ctx.notesHook.loadNotes({ silent: true })
                void ctx.detail.load({ silent: true })
              }}
            />

            <LibertyInternalDocumentsSection
              libertyDocuments={(ctx.documentsHook.libertyDocuments || []) as LibertyDocumentRow[]}
              isExternalInsurance={ctx.isExternalInsurance}
              libertyUploading={ctx.libertyDocumentsBusy}
              uploadsDisabled={!ctx.role.hasPolicy([RESIDENT_POLICIES.defaultFilesView])}
              removesDisabled={!ctx.role.hasPolicy([RESIDENT_POLICIES.defaultFilesRemove])}
              onUploadLibertyDocuments={(files) =>
                void ctx.documentsHook
                  .addLibertyDocuments(files)
                  .then(() => showSuccess('Documents Added Successfully'))
                  .catch(showApiError)
              }
              onOversizedFile={() =>
                showValidationMessage('File Size Exceeds Allowable Limit of 10 MB.')
              }
              onRemoveLibertyDocument={(id) =>
                ctx.setPendingDocumentRemove({ kind: 'liberty', id })
              }
              onViewLibertyDocument={(row) => ctx.downloadLibertyDocument(row)}
              onDownloadLibertyDocument={(row) => ctx.saveLibertyDocument(row)}
              {...(ctx.pageType === 'default-detail'
                ? {
                    oldStatementRows: reimbursementStatementRows(
                      ctx.detail.residentDetail?.document,
                    ),
                    statementUploading: ctx.statementUploading,
                    onUploadStatementDocument: (file: File) =>
                      void ctx.uploadSettlementStatementFiles([file]),
                    onRemoveStatementDocument: (id: string | number) =>
                      ctx.setPendingDocumentRemove({ kind: 'statement', id }),
                    onViewStatementDocument: (row: OldStatementRow) =>
                      ctx.downloadResidentDocument('CLAIM_SETTLEMENT_DOC', row),
                    onDownloadStatementDocument: (row: OldStatementRow) =>
                      ctx.saveResidentDocument('CLAIM_SETTLEMENT_DOC', row),
                  }
                : {})}
            />
          </div>
          ) : (
          <div
            className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 sm:p-6"
            data-testid="resident-detail-libertyinternal-restricted"
          >
            Liberty Internal notes and document uploads are restricted to Liberty roles, matching the Angular
            resident editor.
          </div>
          )
        ) : null}
        {ctx.selectedTab === 'policy' ? (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
            data-testid="resident-detail-policy-section"
          >
            Company-level policy view is read-only.
          </div>
        ) : null}
        {ctx.selectedTab === 'pmsintegration' && ctx.propertyId && ctx.defaultId ? (
          <ResidentPmsIntegrationTab
            propertyId={ctx.propertyId}
            residentDefaultId={ctx.defaultId}
            currentTCode={ctx.detail.residentDetail?.pms_mapping_data?.tCode}
            onUpdated={async () => {
              await ctx.detail.load({ silent: true })
            }}
          />
        ) : null}
        {ctx.showFooterSaveBar && !ctx.isExternalInsurance ? (
          <div
            className="mt-6 flex flex-col-reverse gap-2.5 border-t border-slate-100 pt-4 sm:flex-row sm:flex-wrap sm:justify-end"
            data-testid="resident-detail-footer-save-bar"
          >
            <div className={entityActionButtonClusterClass}>
              <button
                type="button"
                data-testid="resident-detail-footer-cancel-button"
                onClick={() => ctx.form.reset(ctx.detail.formValues)}
                className={secondaryEntityActionButtonClass}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="resident-detail-footer-save-button"
                disabled={saveDisabled}
                className={primaryEntityActionButtonClass}
              >
                Save
              </button>
            </div>
          </div>
        ) : null}
        </div>
      </form>
    </>
  )
}
